import type { FC } from "react";
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { GridPillChip, type BookingRow, type BookingStatus } from "design-components";

export interface DashboardBookingsPreviewProps {
  rows: BookingRow[];
  loading: boolean;
  error: string | null;
}

export const DashboardBookingsPreview: FC<DashboardBookingsPreviewProps> = ({
  rows,
  loading,
  error,
}) => (
  <Box className="operator-dashboard__card operator-dashboard__card--bookings">
    <Typography component="h2" variant="subtitle1" className="operator-dashboard__card-title">
      Bookings
    </Typography>
    {loading ? (
      <Box className="operator-dashboard__loading">
        <CircularProgress aria-label="Loading bookings" size={32} />
      </Box>
    ) : error ? (
      <Typography variant="body2" color="error" className="operator-dashboard__error">
        {error}
      </Typography>
    ) : (
      <Box className="operator-dashboard__table-wrap">
        <Table className="operator-dashboard__table" size="small">

          <TableBody>
            {rows.map((row) => {
              const carrier = row.carrierName?.trim();
              return (
                <TableRow key={row.id}>
                  <TableCell className="operator-dashboard__td">
                    <Link to="/bookings" className="operator-dashboard__booking-id">
                      {row.bookingNumber}
                    </Link>
                  </TableCell>
                  <TableCell className="operator-dashboard__td">{row.customerName || "—"}</TableCell>
                  <TableCell
                    className={
                      carrier
                        ? "operator-dashboard__td"
                        : "operator-dashboard__td operator-dashboard__td--muted"
                    }
                  >
                    {carrier || "Unassigned"}
                  </TableCell>
                  <TableCell className="operator-dashboard__td">{formatEquipType(row)}</TableCell>
                  <TableCell className="operator-dashboard__td">{row.shortId || "—"}</TableCell>
                  <TableCell className="operator-dashboard__td operator-dashboard__td--status">
                    <BookingStatusPill status={row.status} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    )}
    <Box className="operator-dashboard__table-footer">
      <Link to="/bookings" className="operator-dashboard__footer-link">
        View all
      </Link>
    </Box>
  </Box>
);

function formatEquipType(row: BookingRow): string {
  const parts = [row.equipType?.trim(), row.containerSize?.trim()].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

function BookingStatusPill({ status }: { status: BookingStatus }) {
  if (status === "CONFIRMED") {
    return (
      <GridPillChip
        className="users-table__status-pill users-table__status-pill--booking-confirmed operator-dashboard__booking-status-chip"
        pillVariant="positive"
        label="CONFIRMED"
        size="small"
      />
    );
  }
  if (status === "PENDING") {
    return (
      <GridPillChip
        className="users-table__status-pill users-table__status-pill--booking-pending operator-dashboard__booking-status-chip"
        pillVariant="general"
        label="PENDING"
        size="small"
      />
    );
  }
  if (status === "UNASSIGNED") {
    return (
      <GridPillChip
        className="users-table__status-pill users-table__status-pill--booking-unassigned operator-dashboard__booking-status-chip"
        pillVariant="warning"
        label="UNASSIGNED"
        size="small"
      />
    );
  }
  return (
    <GridPillChip
      className="users-table__status-pill users-table__status-pill--booking-complete operator-dashboard__booking-status-chip"
      pillVariant="breeze"
      label="COMPLETE"
      size="small"
    />
  );
}